import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_WIDTH = 250;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 350;

// Chave global para armazenar a largura compartilhada do InfoPanel
const GLOBAL_STORAGE_KEY = "infopanel_width_global";

/**
 * Hook para gerenciar o redimensionamento do InfoPanel (lado direito)
 * Usa uma largura global compartilhada
 */
export const useResizableInfoPanel = (isOpen: boolean) => {
  const [width, setWidth] = useState<number>(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(DEFAULT_WIDTH);

  // Carregar largura global salva do localStorage quando o painel abre
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const savedWidth = localStorage.getItem(GLOBAL_STORAGE_KEY);

    if (savedWidth) {
      const parsedWidth = parseInt(savedWidth, 10);
      // Validar que está dentro dos limites
      if (parsedWidth >= MIN_WIDTH && parsedWidth <= MAX_WIDTH) {
        setWidth(parsedWidth);
        startWidthRef.current = parsedWidth;
      } else {
        // Se estiver fora dos limites, usar valor padrão
        setWidth(DEFAULT_WIDTH);
        startWidthRef.current = DEFAULT_WIDTH;
      }
    } else {
      // Se não houver valor salvo, usar padrão
      setWidth(DEFAULT_WIDTH);
      startWidthRef.current = DEFAULT_WIDTH;
    }
  }, [isOpen]);

  // Iniciar redimensionamento
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width],
  );

  // Resetar para largura padrão
  const resetToDefault = useCallback(() => {
    setWidth(DEFAULT_WIDTH);
    startWidthRef.current = DEFAULT_WIDTH;
    localStorage.setItem(GLOBAL_STORAGE_KEY, DEFAULT_WIDTH.toString());
  }, []);

  // Handler para duplo clique
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resetToDefault();
    },
    [resetToDefault],
  );

  // Redimensionar durante o arraste
  useEffect(() => {
    if (!isResizing) return;

    let currentWidth = startWidthRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      // Para o lado direito, quando arrasta para a esquerda (e.clientX diminui), queremos diminuir a largura
      // Quando arrasta para a direita (e.clientX aumenta), queremos aumentar a largura
      // O handle está na borda esquerda do painel, então quando arrasta para esquerda, o painel fica menor
      const diff = startXRef.current - e.clientX; // Positivo quando arrasta para esquerda, negativo para direita
      const newWidth = Math.max(
        MIN_WIDTH,
        Math.min(MAX_WIDTH, startWidthRef.current + diff),
      );
      currentWidth = newWidth;
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Salvar largura final global no localStorage
      localStorage.setItem(GLOBAL_STORAGE_KEY, currentWidth.toString());
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  return {
    width,
    isResizing,
    handleMouseDown,
    handleDoubleClick,
    resetToDefault,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
  };
};
